const DB_NAME = "bakery-order-cache";
const DB_VERSION = 1;
const STORE_NAME = "pos-product-catalog";

const canUseIndexedDB = () => typeof window !== "undefined" && !!window.indexedDB;

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!canUseIndexedDB()) {
      resolve(null);
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Cannot open product cache"));
  });

const readRecord = async (key) => {
  const database = await openDatabase();
  if (!database) return null;

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error("Cannot read product cache"));
    });
  } finally {
    database.close();
  }
};

const writeRecord = async (record) => {
  const database = await openDatabase();
  if (!database) return;

  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(record);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error("Cannot save product cache"));
      transaction.onabort = () => reject(transaction.error || new Error("Product cache transaction aborted"));
    });
  } finally {
    database.close();
  }
};

const categoriesKey = (scope) => `${scope}:categories`;
const productsKey = (scope, categoryId) => `${scope}:products:${categoryId}`;

export const getProductCacheScope = ({ storeName, storeId, storeType }) =>
  ["pos", storeName || "dongtien", storeId || "unknown", storeType || "unknown"]
    .map((value) => encodeURIComponent(String(value)))
    .join(":");

export const getCachedCategories = async (scope) => {
  const record = await readRecord(categoriesKey(scope));
  return Array.isArray(record?.value) ? record : null;
};

export const saveCategoriesToCache = (scope, categories) =>
  writeRecord({
    key: categoriesKey(scope),
    value: categories,
    updatedAt: new Date().toISOString(),
    count: categories.length,
  });

export const getCachedProducts = async (scope, categoryId) => {
  const record = await readRecord(productsKey(scope, categoryId));
  return Array.isArray(record?.value) ? record : null;
};

export const getAllCachedProducts = async (scope) => {
  const database = await openDatabase();
  if (!database) return [];

  try {
    return await new Promise((resolve, reject) => {
      const prefix = `${scope}:products:`;
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).openCursor(
        IDBKeyRange.bound(prefix, `${prefix}\uffff`),
      );
      const products = [];
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(products);
          return;
        }
        if (Array.isArray(cursor.value?.value)) products.push(...cursor.value.value);
        cursor.continue();
      };
      request.onerror = () => reject(request.error || new Error("Cannot read cached catalog"));
      transaction.onabort = () => reject(transaction.error || new Error("Product cache read aborted"));
    });
  } finally {
    database.close();
  }
};

export const saveProductsToCache = (scope, categoryId, products) =>
  writeRecord({
    key: productsKey(scope, categoryId),
    value: products,
    updatedAt: new Date().toISOString(),
    count: products.length,
  });
